/**
 * LayerSidebar — thin overlay on the left edge of the 3D viewer area.
 *
 * Shows top-level manifest groups with eye-icon toggles. Groups that
 * contain children (slices, isosurfaces) are expandable — each child
 * gets its own visibility toggle. A gear icon opens the detail panel.
 */

import { useCallback, useState, type CSSProperties } from "react";
import type { ManifestLayer } from "./manifestHelpers";

export interface LayerSidebarProps {
  layers: ManifestLayer[];
  visibility: Map<string, boolean>;
  onToggle: (modelId: string, visible: boolean) => void;
  /** Called when the user clicks the gear icon on a group. */
  onOpenPanel?: (layerId: string) => void;
}

const SIDEBAR: CSSProperties = {
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 20,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  background: "var(--color-surface, rgba(17,17,19,0.85))",
  borderRadius: 8,
  border: "1px solid var(--gray-a4)",
  padding: "6px 4px",
  maxHeight: "calc(100% - 16px)",
  overflowY: "auto",
  minWidth: 160,
  backdropFilter: "blur(8px)",
};

const ROW: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 8px",
  borderRadius: 5,
  cursor: "pointer",
  fontSize: 11,
  color: "var(--gray-12)",
  border: "none",
  background: "transparent",
  textAlign: "left",
  width: "100%",
};

const EyeOpen = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ChevronRight = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const GearIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export function LayerSidebar({ layers, visibility, onToggle, onOpenPanel }: LayerSidebarProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = useCallback(
    (id: string) => {
      const current = visibility.get(id) ?? true;
      onToggle(id, !current);
    },
    [visibility, onToggle],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGroupWithChildren = useCallback(
    (layer: ManifestLayer) => {
      const current = visibility.get(layer.id) ?? layer.defaultVisible;
      const next = !current;
      onToggle(layer.id, next);
      if (layer.children) {
        for (const child of layer.children) {
          onToggle(child.id, next);
        }
      }
    },
    [visibility, onToggle],
  );

  if (layers.length === 0) return null;

  return (
    <div style={SIDEBAR}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--gray-9)",
          padding: "2px 8px 4px",
        }}
      >
        Layers
      </span>
      {layers.map((layer) => {
        const visible = visibility.get(layer.id) ?? layer.defaultVisible;
        const hasChildren = layer.children && layer.children.length > 0;
        const isExpanded = expanded.has(layer.id);

        return (
          <div key={layer.id}>
            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
              {/* Expand chevron for groups with children */}
              {hasChildren ? (
                <button
                  style={{
                    background: "none",
                    border: "none",
                    padding: "2px",
                    cursor: "pointer",
                    color: "var(--gray-9)",
                    display: "flex",
                    alignItems: "center",
                    transition: "transform 0.15s ease",
                    transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                    flexShrink: 0,
                  }}
                  onClick={() => toggleExpanded(layer.id)}
                >
                  <ChevronRight />
                </button>
              ) : (
                <span style={{ width: 14, flexShrink: 0 }} />
              )}

              <button
                style={{ ...ROW, opacity: visible ? 1 : 0.5, flex: 1 }}
                onClick={() =>
                  hasChildren
                    ? toggleGroupWithChildren(layer)
                    : toggle(layer.id)
                }
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = "var(--gray-a3)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = "transparent")
                }
              >
                <span style={{ color: visible ? "var(--accent-9)" : "var(--gray-8)", flexShrink: 0 }}>
                  {visible ? <EyeOpen /> : <EyeClosed />}
                </span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {layer.label}
                </span>
              </button>

              {/* Gear icon for groups with children */}
              {hasChildren && onOpenPanel && (
                <button
                  style={{
                    background: "none",
                    border: "none",
                    padding: "3px",
                    cursor: "pointer",
                    color: "var(--gray-8)",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    borderRadius: 3,
                    transition: "color 0.15s ease",
                  }}
                  onClick={() => onOpenPanel(layer.id)}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--gray-12)")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--gray-8)")
                  }
                  title={`Configure ${layer.label}`}
                >
                  <GearIcon />
                </button>
              )}
            </div>

            {/* Expanded children */}
            {hasChildren && isExpanded && (
              <div style={{ paddingLeft: 14 }}>
                {layer.children!.map((child) => {
                  const childVisible = visibility.get(child.id) ?? child.defaultVisible;
                  return (
                    <button
                      key={child.id}
                      style={{
                        ...ROW,
                        opacity: childVisible ? 1 : 0.5,
                        fontSize: 10,
                        padding: "2px 8px",
                      }}
                      onClick={() => toggle(child.id)}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.background = "var(--gray-a3)")
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.background = "transparent")
                      }
                    >
                      <span style={{ color: childVisible ? "var(--accent-9)" : "var(--gray-8)", flexShrink: 0 }}>
                        {childVisible ? <EyeOpen /> : <EyeClosed />}
                      </span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {child.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
